"use client";

import { useEffect, useState } from "react";

export const CONSENT_EVENT = "fp51:consent";
export const OPEN_SETTINGS_EVENT = "fp51:open-cookie-settings";
const CONSENT_STORAGE_KEY = "cookieConsent";

export type ConsentPreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const DEFAULT_PREFS: ConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

function readStoredConsent(): ConsentPreferences | null {
  if (typeof window === "undefined") {
    return null;
  }
  const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentPreferences>;
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
    };
  } catch {
    return null;
  }
}

export function getStoredConsent(): ConsentPreferences {
  return readStoredConsent() ?? DEFAULT_PREFS;
}

function dispatchConsent(prefs: ConsentPreferences) {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<ConsentPreferences>(CONSENT_EVENT, { detail: prefs })
  );
}

function updateConsentMode(prefs: ConsentPreferences) {
  const w = window as unknown as {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  if (!w.gtag) return;
  w.gtag("consent", "update", {
    ad_storage: prefs.marketing ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
  });
}

export default function CookieConsent({ isOpen }: { isOpen: boolean }) {
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(isOpen);
  const [preferences, setPreferences] = useState<ConsentPreferences>(DEFAULT_PREFS);

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) {
      setIsBannerVisible(true);
    } else {
      setPreferences(stored);
      dispatchConsent(stored);
      // Re-afirma el consentimiento en visitas repetidas: gtag.js ya está
      // cargado en el head (Consent Mode v2), así que sin este update los
      // hits seguirían con el default (denied) y no se registrarían.
      updateConsentMode(stored);
    }
  }, []);

  useEffect(() => {
    const onOpenSettings = () => {
      setIsSettingsOpen(true);
      setIsBannerVisible(false);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpenSettings);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpenSettings);
  }, []);

  const savePreferences = () => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences));
    setIsBannerVisible(false);
    setIsSettingsOpen(false);
    dispatchConsent(preferences);
    updateConsentMode(preferences);
  };

  const acceptAll = () => {
    const all: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(all);
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(all));
    setIsBannerVisible(false);
    dispatchConsent(all);
    updateConsentMode(all);
  };

  const rejectAll = () => {
    const none: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(none);
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(none));
    setIsBannerVisible(false);
    dispatchConsent(none);
    updateConsentMode(none);
  };

  if (!isBannerVisible && !isSettingsOpen) return null;

  return (
    <>
      {isBannerVisible && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-4 border-t border-gray-300 z-50">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-sm text-gray-700">
              Usamos cookies propias y de terceros para mejorar tu experiencia y analizar el uso del sitio.{" "}
              <button
                onClick={() => setIsSettingsOpen(true)}
                className="underline text-blue-600"
              >
                Configurar
              </button>
            </p>
            <div className="flex gap-2">
              <button
                onClick={rejectAll}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
              >
                Rechazar
              </button>
              <button
                onClick={acceptAll}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-pink-600 transition"
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}

      {isSettingsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white text-black p-6 rounded-lg max-w-lg w-full">
            <h2 className="text-lg font-bold mb-4">Configuración de Cookies</h2>

            <div className="space-y-4">
              <label className="flex items-center justify-between">
                <span>Cookies necesarias (obligatorias)</span>
                <input type="checkbox" checked disabled />
              </label>

              <label className="flex items-center justify-between">
                <span>Cookies de análisis (GA4)</span>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) =>
                    setPreferences({ ...preferences, analytics: e.target.checked })
                  }
                />
              </label>

              <label className="flex items-center justify-between">
                <span>Cookies de marketing (Google Ads)</span>
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) =>
                    setPreferences({ ...preferences, marketing: e.target.checked })
                  }
                />
              </label>
            </div>

            <div className="flex justify-end mt-6 gap-2">
              <button
                onClick={() => setIsSettingsOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={savePreferences}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-pink-600"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
